import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { fmtFR } from "@/lib/dates";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Trash2, Phone, MessageSquare, MessageCircle, Mail, CheckCircle2,
  Send, Clock, PartyPopper, Sparkles, History, Trophy, ChevronDown, Brain,
  Upload, FileText, Pencil, XCircle, Wallet, Send as SendIcon, RotateCcw
} from "lucide-react";
import { getCategoriesForKind } from "@/constants/categories";
import { toISODate } from "@/lib/time";
import { LeadSelector } from "./EventProductionTab";

const CHANNELS = [
  { v: "tel",                label: "Téléphone",              Icon: Phone },
  { v: "message_repondeur",  label: "Message sur répondeur",  Icon: MessageCircle },
  { v: "sms",                label: "SMS",                    Icon: MessageSquare },
  { v: "mail",               label: "Mail",                   Icon: Mail },
];
const CHANNEL_LABEL = Object.fromEntries(CHANNELS.map(c => [c.v, c.label]));

function EmptyRequest(kind) {
  return {
    kind, name: "", category: "",
    request_date: toISODate(new Date()),
    channel: "mail",
    option_placed: false,
    available: null,
    delay_comment: "",
    received: false,
    received_at: null,
    offer_amount: "",
    offer_comment: "",
  };
}

function AddRequestForm({ kind, onSubmit }) {
  const [f, setF] = useState(EmptyRequest(kind));
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const set = (patch) => setF(x => ({ ...x, ...patch }));
  const isArtist = kind === "artist";
  const targetKind = isArtist ? "artist" : "prestataire";

  // Autocomplete : recherche des fiches existantes au fur et à mesure
  useEffect(() => {
    const q = f.name?.trim();
    if (!q || q.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts", { params: { kind: targetKind, q } });
        setSuggestions(data.slice(0, 8));
      } catch { setSuggestions([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [f.name, targetKind]);

  const pickContact = (c) => {
    setF(x => ({
      ...x,
      name: c.display_name,
      category: c.category || x.category,
      contact_id: c.id,
    }));
    setShowSug(false);
  };

  const submit = async () => {
    if (!f.name.trim()) { toast.error("Nom obligatoire"); return; }
    const payload = { ...f, offer_amount: f.offer_amount === "" ? null : parseFloat(f.offer_amount) };
    await onSubmit(payload);
    setF(EmptyRequest(kind));
    setSuggestions([]);
  };
  return (
    <div className="border border-dashed border-primary/40 rounded-xl p-4 bg-primary/5 space-y-3">
      <div className="text-xs uppercase tracking-widest text-primary font-bold flex items-center gap-1">
        <Plus className="h-3 w-3" /> Ajouter une nouvelle demande
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="relative">
          <Label className="text-xs">{isArtist ? "Nom de l'artiste" : "Nom du prestataire"} *</Label>
          <Input
            value={f.name}
            onChange={e => { set({ name: e.target.value, contact_id: null }); setShowSug(true); }}
            onFocus={() => setShowSug(true)}
            onBlur={() => setTimeout(() => setShowSug(false), 150)}
            autoComplete="off"
            data-testid={`req-${kind}-name`}
          />
          {showSug && suggestions.length > 0 && (
            <div className="absolute z-20 mt-1 w-full rounded-md border border-border bg-card shadow-lg max-h-56 overflow-auto" data-testid={`req-${kind}-suggest`}>
              {suggestions.map(s => (
                <button
                  key={s.id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickContact(s)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-primary/10 border-b last:border-b-0"
                  data-testid={`req-${kind}-suggest-${s.id}`}
                >
                  <div className="font-semibold">{s.display_name}</div>
                  {s.category && <div className="text-[11px] text-muted-foreground">{s.category}</div>}
                </button>
              ))}
            </div>
          )}
          {f.contact_id && (
            <div className="text-[10px] text-emerald-300 mt-1">✓ Fiche existante liée</div>
          )}
          {!f.contact_id && f.name.trim().length >= 2 && suggestions.length === 0 && (
            <div className="text-[10px] text-muted-foreground mt-1">Aucune fiche trouvée — une fiche sera créée automatiquement.</div>
          )}
        </div>
        <div>
          <Label className="text-xs">{isArtist ? "Type d'artiste" : "Nature du prestataire"}</Label>
          <select
            value={f.category}
            onChange={e => set({ category: e.target.value })}
            className="w-full h-10 border border-border rounded-md px-3 bg-background text-sm"
            data-testid={`req-${kind}-category`}
          >
            <option value="">— Choisir une catégorie —</option>
            {getCategoriesForKind(kind).map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs">Date de la demande</Label>
          <Input type="date" value={f.request_date} onChange={e => set({ request_date: e.target.value })} data-testid={`req-${kind}-date`} />
        </div>
        <div>
          <Label className="text-xs">Demande faite par</Label>
          <Select value={f.channel} onValueChange={v => set({ channel: v })}>
            <SelectTrigger data-testid={`req-${kind}-channel`}><SelectValue /></SelectTrigger>
            <SelectContent>{CHANNELS.map(c => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {isArtist && (
          <div>
            <Label className="text-xs">Disponible ?</Label>
            <Select value={f.available === null ? "unknown" : (f.available ? "yes" : "no")} onValueChange={v => set({ available: v === "unknown" ? null : v === "yes" })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unknown">En attente</SelectItem>
                <SelectItem value="yes">Oui</SelectItem>
                <SelectItem value="no">Non</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        <div className={isArtist ? "" : "md:col-span-1"}>
          <Label className="text-xs">Option posée ?</Label>
          <div className="flex items-center gap-2 h-10">
            <Checkbox checked={!!f.option_placed} onCheckedChange={v => set({ option_placed: !!v })} id={`opt-${kind}`} />
            <label htmlFor={`opt-${kind}`} className="text-sm cursor-pointer">Oui, une option est posée</label>
          </div>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">Commentaire sur le délai de réception de l'offre</Label>
          <Input value={f.delay_comment} onChange={e => set({ delay_comment: e.target.value })} placeholder="Ex : retour attendu sous 5 jours" data-testid={`req-${kind}-delay`} />
        </div>
      </div>
      <div className="flex justify-end">
        <Button className="rounded-full bg-primary active:scale-95" onClick={submit} data-testid={`req-${kind}-submit`}>
          <Plus className="h-4 w-4 mr-2" /> Créer la demande
        </Button>
      </div>
    </div>
  );
}

function PendingRequestRow({ req, onMarkReceived, onRemove, onUpdate }) {
  const [amount, setAmount] = useState("");
  const [comment, setComment] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfName, setPdfName] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [paymentMode, setPaymentMode] = useState("facture");   // "facture" | "declaration"
  // Plusieurs déclarations possibles : l'artiste principal + musiciens/technos accompagnants
  const [declarations, setDeclarations] = useState([
    { cachet_type: "net", cachet_amount: "", frais_amount: "" },
  ]);
  const addDeclaration = () => setDeclarations(d => [...d, { cachet_type: "net", cachet_amount: "", frais_amount: "" }]);
  const removeDeclaration = (idx) => setDeclarations(d => d.length > 1 ? d.filter((_, i) => i !== idx) : d);
  const updateDeclaration = (idx, patch) => setDeclarations(d => d.map((x, i) => i === idx ? { ...x, ...patch } : x));
  const [editForm, setEditForm] = useState({
    name: req.name || "", category: req.category || "",
    request_date: req.request_date || "", channel: req.channel || "mail",
    option_placed: !!req.option_placed, available: req.available,
    delay_comment: req.delay_comment || "",
  });
  const Ch = CHANNELS.find(c => c.v === req.channel);
  const ChIcon = Ch?.Icon || Mail;
  const isArtist = req.kind === "artist";
  const startEdit = () => {
    setEditForm({
      name: req.name || "", category: req.category || "",
      request_date: req.request_date || "", channel: req.channel || "mail",
      option_placed: !!req.option_placed, available: req.available,
      delay_comment: req.delay_comment || "",
    });
    setEditing(true);
  };
  const saveEdit = async () => {
    await onUpdate(req, editForm);
    setEditing(false);
  };
  return (
    <div className="border border-border/60 rounded-xl bg-card p-3 flex flex-col gap-2" data-testid={`req-row-${req.id}`}>
      {!editing ? (
        <>
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{req.name}</div>
              <div className="text-xs text-muted-foreground truncate">
                {req.category || "—"}
                {req.request_date && <> · {req.request_date}</>}
                <> · <ChIcon className="inline h-3 w-3 mb-0.5" /> {CHANNEL_LABEL[req.channel] || req.channel}</>
              </div>
              {req.delay_comment && <div className="text-[11px] text-muted-foreground italic mt-0.5">« {req.delay_comment} »</div>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {req.option_placed && <Badge className="rounded-full bg-secondary text-secondary-foreground border-secondary text-[10px]">Option posée</Badge>}
              {req.available === true && <Badge className="rounded-full bg-primary/10 text-primary border-primary/40 text-[10px]">Dispo</Badge>}
              {req.available === false && <Badge variant="outline" className="rounded-full text-destructive border-destructive/40 text-[10px]">Indispo</Badge>}
              <Button variant="ghost" size="icon" className="text-primary hover:bg-primary/10 h-7 w-7" onClick={startEdit} data-testid={`req-edit-${req.id}`} title="Modifier la demande">
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="icon" className="text-destructive hover:bg-destructive/10 h-7 w-7" onClick={() => onRemove(req)} data-testid={`req-remove-${req.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          {!open ? (
            <Button variant="outline" size="sm" className="rounded-full self-end" onClick={() => setOpen(true)} data-testid={`req-received-${req.id}`}>
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Marquer offre reçue
            </Button>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end pt-2 border-t border-border/40">
              <div>
                <Label className="text-[10px]">Montant HT (€)</Label>
                <Input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" data-testid={`req-amount-${req.id}`} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[10px]">Commentaire</Label>
                <Input value={comment} onChange={e => setComment(e.target.value)} placeholder="Détails de l'offre reçue" />
              </div>
              {isArtist && (
                <div className="md:col-span-3 rounded-lg border border-primary/40 bg-primary/5 p-2 space-y-2" data-testid={`req-payment-${req.id}`}>
                  <div className="text-[10px] uppercase tracking-widest text-primary font-bold">Paiement de l'artiste</div>
                  <div className="flex items-center gap-4 text-xs">
                    <label className="inline-flex items-center gap-1 cursor-pointer">
                      <input type="radio" name={`pay-${req.id}`} value="facture" checked={paymentMode === "facture"} onChange={() => setPaymentMode("facture")} data-testid={`req-pay-facture-${req.id}`} />
                      <span>Sur facture</span>
                    </label>
                    <label className="inline-flex items-center gap-1 cursor-pointer">
                      <input type="radio" name={`pay-${req.id}`} value="declaration" checked={paymentMode === "declaration"} onChange={() => setPaymentMode("declaration")} data-testid={`req-pay-declaration-${req.id}`} />
                      <span>Sur déclaration</span>
                    </label>
                  </div>
                  {paymentMode === "declaration" && (
                    <div className="space-y-2 pt-1">
                      {declarations.map((d, idx) => (
                        <div key={idx} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end" data-testid={`req-decl-row-${req.id}-${idx}`}>
                          <div>
                            <Label className="text-[10px]">
                              Déclaration {idx + 1}{idx === 0 ? " (artiste)" : " (accompagnant)"} · Type
                            </Label>
                            <select value={d.cachet_type} onChange={e => updateDeclaration(idx, { cachet_type: e.target.value })} className="w-full h-9 border border-border rounded-md px-2 bg-card text-xs" data-testid={`req-cachet-type-${req.id}-${idx}`}>
                              <option value="net">Cachet net</option>
                              <option value="brut">Cachet brut</option>
                            </select>
                          </div>
                          <div>
                            <Label className="text-[10px]">Montant cachet {d.cachet_type} (€)</Label>
                            <Input type="number" step="0.01" value={d.cachet_amount} onChange={e => updateDeclaration(idx, { cachet_amount: e.target.value })} placeholder="0.00" data-testid={`req-cachet-amount-${req.id}-${idx}`} />
                          </div>
                          <div>
                            <Label className="text-[10px]">Frais (€)</Label>
                            <Input type="number" step="0.01" value={d.frais_amount} onChange={e => updateDeclaration(idx, { frais_amount: e.target.value })} placeholder="0.00" data-testid={`req-frais-amount-${req.id}-${idx}`} />
                          </div>
                          {declarations.length > 1 && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:bg-destructive/10 h-9 w-9"
                              onClick={() => removeDeclaration(idx)}
                              data-testid={`req-decl-remove-${req.id}-${idx}`}
                              title="Retirer cette déclaration"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full h-7 text-[11px] border-primary/40 text-primary hover:bg-primary/10"
                        onClick={addDeclaration}
                        data-testid={`req-decl-add-${req.id}`}
                      >
                        <Plus className="h-3 w-3 mr-1" /> Ajouter une déclaration
                      </Button>
                    </div>
                  )}
                </div>
              )}
              <div className="md:col-span-3">
                <Label className="text-[10px]">PDF du devis (optionnel)</Label>
                <label className="mt-1 flex items-center gap-2 rounded-md border-2 border-dashed border-primary/40 bg-primary/5 px-3 py-2 cursor-pointer hover:bg-primary/10 transition" data-testid={`req-pdf-label-${req.id}`}>
                  <Upload className="h-4 w-4 text-primary shrink-0" />
                  <span className="flex-1 text-xs truncate">
                    {pdfFile ? (
                      <span className="font-semibold text-primary">{pdfFile.name}</span>
                    ) : (
                      <span className="text-muted-foreground">Cliquez pour joindre le PDF du devis {req.kind === "artist" ? "de l'artiste" : "du prestataire"}</span>
                    )}
                  </span>
                  <input
                    type="file"
                    accept="application/pdf,image/*"
                    className="hidden"
                    onChange={e => {
                      const f = e.target.files?.[0] || null;
                      setPdfFile(f);
                      if (f && !pdfName) setPdfName((f.name || "").replace(/\.pdf$/i, ""));
                    }}
                    data-testid={`req-pdf-input-${req.id}`}
                  />
                </label>
                {pdfFile && (
                  <Input
                    className="mt-1 h-8 text-xs"
                    value={pdfName}
                    onChange={e => setPdfName(e.target.value)}
                    placeholder="Nom à afficher (ex : Devis DJ v1)"
                    data-testid={`req-pdf-name-${req.id}`}
                  />
                )}
              </div>
              <div className="md:col-span-3 flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => { setOpen(false); setAmount(""); setComment(""); setPdfFile(null); setPdfName(""); setPaymentMode("facture"); setDeclarations([{ cachet_type: "net", cachet_amount: "", frais_amount: "" }]); }}>Annuler</Button>
                <Button size="sm" className="rounded-full bg-primary" onClick={async () => {
                  const files = [];
                  if (pdfFile) {
                    const nm = (pdfName || pdfFile.name.replace(/\.pdf$/i, "")).trim();
                    if (!nm) { toast.error("Nom du PDF requis"); return; }
                    files.push({ file: pdfFile, name: nm });
                  }
                  const cleanDecls = declarations.map(d => ({
                    cachet_type: d.cachet_type || "net",
                    cachet_amount: d.cachet_amount !== "" ? parseFloat(d.cachet_amount) : null,
                    frais_amount: d.frais_amount !== "" ? parseFloat(d.frais_amount) : null,
                  }));
                  const first = cleanDecls[0] || {};
                  const extraPay = isArtist ? {
                    payment_mode: paymentMode,
                    // Miroir de la 1ère déclaration pour rétro-compatibilité
                    cachet_type: paymentMode === "declaration" ? (first.cachet_type || null) : null,
                    cachet_amount: paymentMode === "declaration" ? (first.cachet_amount ?? null) : null,
                    frais_amount: paymentMode === "declaration" ? (first.frais_amount ?? null) : null,
                    declarations: paymentMode === "declaration" ? cleanDecls : [],
                  } : {};
                  await onMarkReceived(req, amount, comment, files, extraPay);
                  setOpen(false); setAmount(""); setComment(""); setPdfFile(null); setPdfName("");
                  setPaymentMode("facture");
                  setDeclarations([{ cachet_type: "net", cachet_amount: "", frais_amount: "" }]);
                }} data-testid={`req-confirm-${req.id}`}>
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Valider la réception
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-2 rounded-lg bg-primary/5 border border-primary/30" data-testid={`req-edit-form-${req.id}`}>
          <div className="text-[10px] uppercase tracking-widest text-primary font-bold md:col-span-2">Modification de la demande</div>
          <div><Label className="text-[10px]">Nom</Label><Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} /></div>
          <div>
            <Label className="text-[10px]">{isArtist ? "Type d'artiste" : "Nature"}</Label>
            <select value={editForm.category} onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))} className="w-full h-10 border border-border rounded-md px-3 bg-background text-sm">
              <option value="">— Choisir —</option>
              {getCategoriesForKind(req.kind).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div><Label className="text-[10px]">Date demande</Label><Input type="date" value={editForm.request_date} onChange={e => setEditForm(f => ({ ...f, request_date: e.target.value }))} /></div>
          <div>
            <Label className="text-[10px]">Canal</Label>
            <select value={editForm.channel} onChange={e => setEditForm(f => ({ ...f, channel: e.target.value }))} className="w-full h-10 border border-border rounded-md px-3 bg-background text-sm">
              {CHANNELS.map(c => <option key={c.v} value={c.v}>{c.label}</option>)}
            </select>
          </div>
          {isArtist && (
            <div>
              <Label className="text-[10px]">Disponible ?</Label>
              <select value={editForm.available === null || editForm.available === undefined ? "unknown" : (editForm.available ? "yes" : "no")}
                onChange={e => setEditForm(f => ({ ...f, available: e.target.value === "unknown" ? null : e.target.value === "yes" }))}
                className="w-full h-10 border border-border rounded-md px-3 bg-background text-sm">
                <option value="unknown">En attente</option>
                <option value="yes">Oui</option>
                <option value="no">Non</option>
              </select>
            </div>
          )}
          <div className="flex items-center gap-2 mt-4">
            <Checkbox checked={!!editForm.option_placed} onCheckedChange={v => setEditForm(f => ({ ...f, option_placed: !!v }))} id={`edit-opt-${req.id}`} />
            <label htmlFor={`edit-opt-${req.id}`} className="text-sm">Option posée</label>
          </div>
          <div className="md:col-span-2"><Label className="text-[10px]">Commentaire délai</Label><Input value={editForm.delay_comment} onChange={e => setEditForm(f => ({ ...f, delay_comment: e.target.value }))} /></div>
          <div className="md:col-span-2 flex justify-end gap-2">
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => setEditing(false)}>Annuler</Button>
            <Button size="sm" className="rounded-full bg-primary" onClick={saveEdit} data-testid={`req-edit-save-${req.id}`}>
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Enregistrer
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ReceivedList({ items, onRemove, onDecision, onValidateWithDeposit, onEdit, onUploadFile, onDeleteFile }) {
  if (!items.length) {
    return <div className="text-sm text-muted-foreground text-center py-4 italic">Aucune offre reçue</div>;
  }
  const groups = items.reduce((acc, r) => {
    const k = r.category || "Autres";
    (acc[k] = acc[k] || []).push(r);
    return acc;
  }, {});
  const apiBase = process.env.REACT_APP_BACKEND_URL + "/api";
  const BADGE = {
    pending:   { label: "Reçue",   cls: "bg-secondary/20 text-secondary-foreground border-secondary/60" },
    validated: { label: "Validée", cls: "bg-emerald-500/15 text-emerald-200 border-emerald-500/50" },
    refused:   { label: "Refusée", cls: "bg-rose-500/15 text-rose-200 border-rose-500/50" },
  };
  return (
    <div className="space-y-3">
      {Object.entries(groups).map(([cat, list]) => (
        <div key={cat}>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1">{cat}</div>
          <div className="space-y-2">
            {list.map(r => {
              const dec = r.offer_decision || "pending";
              const files = r.offer_files || [];
              return (
                <div key={r.id} className="border border-primary/30 bg-primary/5 rounded-xl p-3" data-testid={`recv-row-${r.id}`}>
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-medium truncate">{r.name}</div>
                        <Badge className={`rounded-full text-[10px] border ${BADGE[dec].cls}`} data-testid={`recv-status-${r.id}`}>
                          {BADGE[dec].label}
                        </Badge>
                        {files.length > 0 && (
                          <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
                            <FileText className="h-3 w-3" /> {files.length} devis
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        Reçue le {r.received_at || "—"}
                        {r.offer_comment && <> · « {r.offer_comment} »</>}
                        {r.offer_decision_at && dec !== "pending" && <> · {dec === "validated" ? "Validée" : "Refusée"} le {r.offer_decision_at}</>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-brand text-lg tabular-nums text-primary">{r.offer_amount ? `${Number(r.offer_amount).toLocaleString("fr-FR")} €` : "—"}</div>
                      <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-bold">HT</div>
                    </div>
                  </div>

                  {/* Liste des devis PDF attachés */}
                  {files.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {files.map(f => (
                        <div key={f.id} className="flex items-center gap-2 text-xs bg-card border border-border/50 rounded-md px-2 py-1" data-testid={`recv-file-${f.id}`}>
                          <FileText className="h-3 w-3 text-muted-foreground" />
                          <div className="flex-1 truncate">{f.name} <span className="text-muted-foreground">· {Math.round((f.size||0)/1024)} Ko</span></div>
                          <a href={`${apiBase}/files/${f.file_id}`} target="_blank" rel="noreferrer" className="text-primary underline">Ouvrir</a>
                          <button onClick={() => onDeleteFile(r, f)} className="text-destructive hover:bg-destructive/10 rounded p-1">
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="mt-2 flex flex-wrap items-center gap-2 justify-end">
                    {dec === "refused" ? (
                      <>
                        {r.offer_decision_reason && (
                          <div className="text-[11px] italic text-rose-300 mr-auto">Motif : « {r.offer_decision_reason} »</div>
                        )}
                        <Button size="sm" variant="outline" className="rounded-full h-7 text-[11px]" onClick={() => onDecision(r, "pending")} data-testid={`recv-unrefuse-${r.id}`}>
                          <RotateCcw className="h-3 w-3 mr-1" /> Annuler le refus
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full h-7 text-[11px] border-destructive text-destructive hover:bg-destructive/10 font-semibold" onClick={() => onRemove(r)} data-testid={`recv-delete-refused-${r.id}`}>
                          <Trash2 className="h-3 w-3 mr-1" /> Supprimer la demande
                        </Button>
                      </>
                    ) : (
                      <>
                        <label className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2 h-7 text-[11px] cursor-pointer hover:bg-muted" data-testid={`recv-upload-${r.id}`}>
                          <Upload className="h-3 w-3" /> Ajouter un devis PDF
                          <input type="file" accept="application/pdf,image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) onUploadFile(r, f); e.target.value=""; }} />
                        </label>
                        <Button size="sm" variant="outline" className="rounded-full h-7 text-[11px]" onClick={() => onEdit(r)} data-testid={`recv-edit-${r.id}`}>
                          <Pencil className="h-3 w-3 mr-1" /> Modifier
                        </Button>
                        <Button size="sm" className={`rounded-full h-7 text-[11px] ${dec === "validated" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-emerald-500 hover:bg-emerald-600"} text-white`} onClick={() => onValidateWithDeposit(r)} data-testid={`recv-validate-${r.id}`}>
                          <CheckCircle2 className="h-3 w-3 mr-1" /> Valider
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full h-7 text-[11px] border-rose-500/50 text-rose-300 hover:bg-rose-500/15" onClick={() => onDecision(r, "refused")} data-testid={`recv-refuse-${r.id}`}>
                          <XCircle className="h-3 w-3 mr-1" /> Refuser
                        </Button>
                        <Button variant="ghost" size="icon" className="text-destructive hover:bg-destructive/10 h-7 w-7" onClick={() => onRemove(r)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function BrainstormingCard({ event, reload }) {
  const [value, setValue] = useState(event.brainstorming || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setValue(event.brainstorming || ""); }, [event.id]);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/events/${event.id}`, { brainstorming: value });
      toast.success("Idées enregistrées");
      await reload(true);
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };
  const dirty = (event.brainstorming || "") !== value;
  return (
    <Card className="border-border/60 shadow-warm" data-testid="brainstorming-card">
      <CardContent className="p-5 md:p-6">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 text-primary" />
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Idées / Brainstorming</div>
          {dirty && <span className="text-[10px] text-secondary-foreground italic ml-auto">Modifications non enregistrées</span>}
        </div>
        <textarea
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder="Notez ici toutes les idées, ambiances, références, contraintes techniques, envies du client…"
          rows={5}
          className="w-full rounded-xl border border-border bg-card p-3 text-sm resize-y focus:border-primary/40 focus:outline-none"
          data-testid="brainstorming-input"
        />
        <div className="flex justify-end mt-2">
          <Button size="sm" className="rounded-full bg-primary active:scale-95" onClick={save} disabled={saving || !dirty} data-testid="brainstorming-save">
            {saving ? "Enregistrement…" : "Enregistrer les idées"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function EventCommercialisationTab({ event, reload }) {
  const providerReqs = event.provider_requests || [];
  const artistReqs = event.artist_requests || [];
  const providerPending = providerReqs.filter(r => !r.received);
  const providerReceived = providerReqs.filter(r => r.received);
  const artistPending = artistReqs.filter(r => !r.received);
  const artistReceived = artistReqs.filter(r => r.received);

  const [offer, setOffer] = useState({
    sent_at: event.client_offer?.sent_at || "",
    amount: event.client_offer?.amount ?? "",
    follow_up_at: event.client_offer?.follow_up_at || "",
    files: [],
  });
  const versions = event.client_offer?.versions || [];
  const acceptedVersion = event.client_offer?.accepted_version || "";
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [newVersion, setNewVersion] = useState({ sent_at: "", amount: "", follow_up_at: "", note: "" });
  const [savingVersion, setSavingVersion] = useState(false);
  const [acceptedVersionChoice, setAcceptedVersionChoice] = useState("");

  const openNewVersion = () => {
    const nextNum = versions.length + 1;
    setNewVersion({
      sent_at: toISODate(new Date()),
      amount: event.client_offer?.amount ?? "",
      follow_up_at: "",
      note: "",
      version: `v${nextNum}`,
      file: null,
    });
    setVersionDialogOpen(true);
  };
  const submitNewVersion = async () => {
    if (!newVersion.sent_at) { toast.error("Date d'envoi requise"); return; }
    if (newVersion.amount === "" || isNaN(parseFloat(newVersion.amount))) { toast.error("Montant HT requis"); return; }
    if (!newVersion.file) { toast.error("PDF du devis obligatoire"); return; }
    setSavingVersion(true);
    try {
      const fd = new FormData();
      fd.append("version", newVersion.version || "");
      fd.append("sent_at", newVersion.sent_at);
      fd.append("amount", String(parseFloat(newVersion.amount)));
      if (newVersion.follow_up_at) fd.append("follow_up_at", newVersion.follow_up_at);
      fd.append("note", newVersion.note || "");
      fd.append("file", newVersion.file);
      await api.post(`/events/${event.id}/client-offer/versions/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Version ${newVersion.version} envoyée avec devis PDF`);
      setVersionDialogOpen(false);
      await reload();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSavingVersion(false); }
  };

  const addRequest = async (payload) => {
    // Reload silencieux (sans spinner) pour préserver la position de scroll
    // — sinon l'écran remontait tout en haut après création.
    try { await api.post(`/events/${event.id}/requests`, payload); toast.success("Demande créée"); await reload(true); }
    catch (e) { toast.error(extractError(e)); }
  };
  const markReceived = async (req, amount, comment, files = [], extraPay = {}) => {
    try {
      await api.patch(`/events/${event.id}/requests/${req.id}`, {
        ...req, kind: req.kind, received: true, received_at: toISODate(new Date()),
        offer_amount: amount === "" ? null : parseFloat(amount),
        offer_comment: comment,
        ...extraPay,
      });
      // Upload PDF(s) éventuel(s)
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f.file);
        fd.append("name", f.name.trim());
        await api.post(`/events/${event.id}/requests/${req.id}/offer-files?kind=${req.kind}&name=${encodeURIComponent(f.name.trim())}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(files.length ? `Offre reçue + ${files.length} devis ajouté${files.length > 1 ? "s" : ""}` : "Offre marquée reçue");
      await reload(true);
    } catch (e) { toast.error(extractError(e)); }
  };
  const setDecision = async (req, decision, depositAmount = null) => {
    if (decision === "refused" && !confirm(`Refuser l'offre de "${req.name}" ?`)) return;
    try {
      const params = { kind: req.kind, decision };
      if (decision === "validated" && depositAmount !== null && depositAmount !== "" && !isNaN(parseFloat(depositAmount))) {
        params.deposit_amount = parseFloat(depositAmount);
      }
      await api.post(`/events/${event.id}/requests/${req.id}/decision`, null, { params });
      toast.success(decision === "validated"
        ? (params.deposit_amount ? `Offre validée · Acompte ${params.deposit_amount} € à payer` : "Offre validée")
        : "Offre refusée");
      await reload(true);
    } catch (e) { toast.error(extractError(e)); }
  };
  // État du dialog d'acompte à la validation
  const [depositDialog, setDepositDialog] = useState({ open: false, req: null, amount: "" });
  const openValidateDialog = (req) => setDepositDialog({ open: true, req, amount: req.deposit_amount ?? "" });
  const confirmValidateWithDeposit = async () => {
    const { req, amount } = depositDialog;
    setDepositDialog({ open: false, req: null, amount: "" });
    if (req) await setDecision(req, "validated", amount);
  };
  const uploadOfferFile = async (req, file) => {
    const nm = window.prompt("Nom du devis PDF (obligatoire) :", file.name.replace(/\.pdf$/i, ""));
    if (!nm || !nm.trim()) return;
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("name", nm.trim());
      await api.post(`/events/${event.id}/requests/${req.id}/offer-files?kind=${req.kind}&name=${encodeURIComponent(nm.trim())}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Devis ajouté" + (req.contact_id ? " (aussi à la fiche prestataire)" : ""));
      await reload(true);
    } catch (e) { toast.error(extractError(e)); }
  };
  const deleteOfferFile = async (req, f) => {
    if (!confirm(`Supprimer « ${f.name} » ?`)) return;
    try {
      await api.delete(`/events/${event.id}/requests/${req.id}/offer-files/${f.id}`, {
        params: { kind: req.kind },
      });
      toast.success("Devis supprimé");
      await reload(true);
    } catch (e) { toast.error(extractError(e)); }
  };
  const editReceived = async (req) => {
    const amount = window.prompt("Nouveau montant HT (€) :", req.offer_amount ?? "");
    if (amount === null) return;
    const comment = window.prompt("Commentaire :", req.offer_comment || "");
    if (comment === null) return;
    try {
      await api.patch(`/events/${event.id}/requests/${req.id}`, {
        ...req, kind: req.kind,
        offer_amount: amount === "" ? null : parseFloat(amount),
        offer_comment: comment,
      });
      toast.success("Offre modifiée");
      await reload(true);
    } catch (e) { toast.error(extractError(e)); }
  };
  const removeRequest = async (req) => {
    if (!confirm(`Supprimer "${req.name}" ?`)) return;
    try { await api.delete(`/events/${event.id}/requests/${req.id}`, { params: { kind: req.kind } }); toast.success("Supprimée"); await reload(true); }
    catch (e) { toast.error(extractError(e)); }
  };

  // Édition inline d'une demande en attente (non reçue)
  const updatePending = async (req, patch) => {
    try {
      await api.patch(`/events/${event.id}/requests/${req.id}`, {
        ...req, kind: req.kind,
        name: (patch.name ?? req.name).trim(),
        category: patch.category ?? req.category,
        request_date: patch.request_date ?? req.request_date,
        channel: patch.channel ?? req.channel,
        option_placed: !!(patch.option_placed ?? req.option_placed),
        available: patch.available === undefined ? req.available : patch.available,
        delay_comment: patch.delay_comment ?? req.delay_comment,
      });
      toast.success("Demande mise à jour");
      await reload(true);
    } catch (e) { toast.error(extractError(e)); }
  };

  // Demande de validation interne du devis auprès de l'administrateur
  const requestQuoteReview = async () => {
    if (!confirm("Envoyer une demande de validation du devis à l'administrateur ?")) return;
    try {
      await api.post(`/events/${event.id}/quote-review-request`);
      toast.success("Demande envoyée à l'administrateur");
      await reload(true);
    } catch (e) { toast.error(extractError(e)); }
  };

  // Annulation de la demande de validation interne
  const cancelQuoteReview = async () => {
    if (!confirm("Annuler la demande de validation du devis ? La carte repasse en « Devis à faire ».")) return;
    try {
      await api.delete(`/events/${event.id}/quote-review-request`);
      toast.success("Demande de validation annulée");
      await reload(true);
    } catch (e) { toast.error(extractError(e)); }
  };

  const saveOffer = async () => {
    if (!offer.sent_at) { toast.error("Date d'envoi requise"); return; }
    if (offer.amount === "" || isNaN(parseFloat(offer.amount))) { toast.error("Montant HT requis"); return; }
    if (!offer.files || offer.files.length === 0) { toast.error("Au moins un PDF du devis est requis"); return; }
    try {
      const fd = new FormData();
      const nextNum = (versions?.length || 0) + 1;
      fd.append("version", `v${nextNum}`);
      fd.append("sent_at", offer.sent_at);
      fd.append("amount", String(parseFloat(offer.amount)));
      if (offer.follow_up_at) fd.append("follow_up_at", offer.follow_up_at);
      (offer.files || []).forEach(f => fd.append("files", f));
      await api.post(`/events/${event.id}/client-offer/versions/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Offre envoyée — version v${nextNum} archivée dans l'historique`);
      setOffer(o => ({ ...o, files: [] }));
      await reload();
    } catch (e) { toast.error(extractError(e)); }
  };
  const cancelVersion = async (v) => {
    if (!window.confirm(`Annuler l'envoi ${v.version} ? Cette version sera retirée de l'historique.`)) return;
    try {
      await api.delete(`/events/${event.id}/client-offer/versions/${v.version}`);
      toast.success(`Envoi ${v.version} annulé`);
      await reload();
    } catch (e) { toast.error(extractError(e)); }
  };
  const [noteEdit, setNoteEdit] = useState({});   // {[version]: string}
  const saveVersionNote = async (v) => {
    const note = noteEdit[v.version] ?? v.note ?? "";
    try {
      await api.patch(`/events/${event.id}/client-offer/versions/${v.version}`, { note });
      setNoteEdit(prev => { const n = { ...prev }; delete n[v.version]; return n; });
      toast.success(`Note ${v.version} enregistrée`);
      await reload();
    } catch (e) { toast.error(extractError(e)); }
  };
  const acceptOffer = async () => {
    // Si plusieurs versions, demander laquelle a été acceptée
    let version = acceptedVersionChoice;
    if (versions.length > 1 && !version) {
      version = versions[versions.length - 1].version;
    }
    if (!confirm(`Confirmer que le client a accepté ${version ? `la version ${version}` : "l'offre"} ? (l'étape « Devis validé » et « Feuille de route à faire » seront automatiquement cochées)`)) return;
    try {
      await api.post(`/events/${event.id}/client-offer/accept`, null, {
        params: version ? { accepted_version: version } : {},
      });
      // Célébration plein écran : étoiles qui pleuvent + bannière
      triggerDevisAcceptedAnimation();
      toast.success("Devis accepté 🎉 — passage automatique en Production");
      await reload(true);
    } catch (e) { toast.error(extractError(e)); }
  };

  const unacceptOffer = async () => {
    if (!confirm("Annuler la validation du devis par le client ? L'étape « Devis validé » sera décochée et l'événement repasse en Commercialisation.")) return;
    try {
      await api.post(`/events/${event.id}/client-offer/unaccept`);
      toast.success("Validation client annulée — retour en Commercialisation");
      await reload(true);
    } catch (e) { toast.error(extractError(e)); }
  };

  const [refuseDialog, setRefuseDialog] = useState({ open: false, version: null, mode: "new_version", reason: "" });
  const openRefuseDialog = (v) => setRefuseDialog({ open: true, version: v?.version || null, mode: "new_version", reason: "" });
  const submitRefuse = async () => {
    const { version, mode, reason } = refuseDialog;
    if (mode === "final" && !confirm("Confirmer le refus DÉFINITIF du devis par le client ? L'événement passera en « Événement annulé ».")) return;
    try {
      await api.post(`/events/${event.id}/client-offer/refuse`, { version, mode, reason });
      setRefuseDialog({ open: false, version: null, mode: "new_version", reason: "" });
      toast.success(mode === "final" ? "Événement passé en « Événement annulé »" : "Version marquée comme refusée — vous pouvez envoyer une nouvelle version");
      await reload(true);
    } catch (e) { toast.error(extractError(e)); }
  };

  function triggerDevisAcceptedAnimation() {
    const layer = document.createElement("div");
    layer.className = "devis-accepted-overlay";
    layer.setAttribute("data-testid", "devis-accepted-overlay");
    const banner = document.createElement("div");
    banner.className = "devis-accepted-banner";
    banner.innerText = "🎉  DEVIS VALIDÉ !";
    layer.appendChild(banner);
    const emojis = ["✨", "🌟", "🎉", "⭐", "💫", "🏆"];
    for (let i = 0; i < 42; i++) {
      const s = document.createElement("span");
      s.className = "devis-particle";
      s.style.left = `${Math.random() * 100}%`;
      s.style.animationDelay = `${Math.random() * 0.9}s`;
      s.style.animationDuration = `${1.6 + Math.random() * 1.6}s`;
      s.innerText = emojis[Math.floor(Math.random() * emojis.length)];
      layer.appendChild(s);
    }
    document.body.appendChild(layer);
    // Retrait sécurisé : vérifie que le nœud est toujours enfant du body
    // (peut être retiré par un unmount ou une extension DOM externe).
    setTimeout(() => {
      try { if (layer.parentNode) layer.parentNode.removeChild(layer); }
      catch { /* ignore */ }
    }, 3200);
  }

  const setCheckpoint = async (key, done) => {
    try {
      await api.patch(`/events/${event.id}/checkpoint`, { key, done });
      await reload();
    } catch (e) { toast.error(extractError(e)); }
  };
  const devisPret = !!(event.checkpoints?.devis_pret?.done);

  return (
    <div className="space-y-6" data-testid="commerc-tab">
      {/* Chargé de commercialisation */}
      <LeadSelector
        event={event}
        reload={reload}
        field="commercial_lead_id"
        currentName={event.commercial_lead_name}
        label="Chargé de commercialisation"
        subtitle="Membre AAE responsable de la phase commercialisation. Sera remplacé par le chargé de projet au passage en Production."
        testId="commercial-lead"
      />

      {/* Dates clés */}
      <Card className="border-border/60 shadow-warm">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Date de demande de remise du devis</div>
              <div className="font-medium tabular-nums mt-1">{event.demande?.quote_request_date ? fmtFR(event.demande.quote_request_date) : "—"}</div>
              {event.demande?.quote_request_comment && <div className="text-xs text-muted-foreground italic mt-0.5">« {event.demande.quote_request_comment} »</div>}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Date de prise de décision</div>
              <div className="font-medium tabular-nums mt-1">{event.demande?.decision_date ? fmtFR(event.demande.decision_date) : "—"}</div>
              {event.demande?.decision_comment && <div className="text-xs text-muted-foreground italic mt-0.5">« {event.demande.decision_comment} »</div>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Alerte : prioriser les demandes prestataires/artistes */}
      <div className="rounded-lg border-2 border-amber-400 bg-amber-500/15 px-4 py-3 text-sm text-amber-200 font-semibold" data-testid="commerc-priority-warning">
        ⚠️ Faire toutes ses demandes prestataires / artistes en priorité pour pouvoir avancer sur le reste en attendant les réponses.
      </div>

      {/* Idées / Brainstorming — juste sous les dates clés */}
      <BrainstormingCard event={event} reload={reload} />

      {/* Prestataires — en attente */}
      <Card className="border-border/60 shadow-warm">
        <CardContent className="p-5 space-y-3">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold flex items-center justify-between">
            <span className="flex items-center gap-1"><Clock className="h-3 w-3 text-secondary-foreground" /> Devis prestataires en attente</span>
            <Badge variant="outline" className="rounded-full">{providerPending.length}</Badge>
          </div>
          <AddRequestForm kind="provider" onSubmit={addRequest} />
          <div className="space-y-2">
            {providerPending.length === 0
              ? <div className="text-sm text-muted-foreground text-center py-4 italic">Aucune demande en attente</div>
              : providerPending.map(r => (
                  <PendingRequestRow key={r.id} req={{ ...r, kind: "provider" }} onMarkReceived={markReceived} onRemove={removeRequest} onUpdate={updatePending} />
                ))}
          </div>
        </CardContent>
      </Card>

      {/* Artistes — en attente */}
      <Card className="border-border/60 shadow-warm">
        <CardContent className="p-5 space-y-3">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold flex items-center justify-between">
            <span className="flex items-center gap-1"><Clock className="h-3 w-3 text-secondary-foreground" /> Demandes artistes en attente</span>
            <Badge variant="outline" className="rounded-full">{artistPending.length}</Badge>
          </div>
          <AddRequestForm kind="artist" onSubmit={addRequest} />
          <div className="space-y-2">
            {artistPending.length === 0
              ? <div className="text-sm text-muted-foreground text-center py-4 italic">Aucune demande en attente</div>
              : artistPending.map(r => (
                  <PendingRequestRow key={r.id} req={{ ...r, kind: "artist" }} onMarkReceived={markReceived} onRemove={removeRequest} onUpdate={updatePending} />
                ))}
          </div>
        </CardContent>
      </Card>

      {/* Devis réceptionnés */}
      <Card className="border-border/60 shadow-warm">
        <CardContent className="p-5 space-y-4">
          <div className="text-xs uppercase tracking-widest text-primary font-bold flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" /> Devis réceptionnés
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold mb-2">Prestataires ({providerReceived.length})</div>
            <ReceivedList items={providerReceived.map(r => ({ ...r, kind: "provider" }))} onRemove={removeRequest} onDecision={setDecision} onValidateWithDeposit={openValidateDialog} onEdit={editReceived} onUploadFile={uploadOfferFile} onDeleteFile={deleteOfferFile} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold mb-2">Artistes ({artistReceived.length})</div>
            <ReceivedList items={artistReceived.map(r => ({ ...r, kind: "artist" }))} onRemove={removeRequest} onDecision={setDecision} onValidateWithDeposit={openValidateDialog} onEdit={editReceived} onUploadFile={uploadOfferFile} onDeleteFile={deleteOfferFile} />
          </div>
        </CardContent>
      </Card>

      {/* Offre envoyée au client */}
      <Card className="border-border/60 shadow-warm">
        <CardContent className="p-5 space-y-3">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1">
            <Send className="h-3 w-3 text-primary" /> Offre envoyée au client
          </div>

          {/* Feux tricolores Axonaut : préconditions avant demande de validation */}
          {(() => {
            const costsOk = !!event.axonaut_costs_ok;
            const prodsOk = !!event.axonaut_products_ok;
            const folderOk = !!event.axonaut_folder_ok;
            const toggle = async (field, val) => {
              try {
                await api.patch(`/events/${event.id}/axonaut-check`, { [field]: val });
                await reload(true);
              } catch (e) { toast.error(extractError(e)); }
            };
            const Row = ({ ok, label, onToggle, testId, extra }) => (
              <div className={`flex items-center gap-3 rounded-lg border-2 px-3 py-2 ${ok ? "border-emerald-500 bg-emerald-500/15" : "border-red-400 bg-red-500/15"}`} data-testid={testId}>
                <button type="button" onClick={() => onToggle(!ok)}
                  className={`h-8 w-8 rounded-full shrink-0 border-2 transition active:scale-90 ${ok ? "bg-emerald-500 border-emerald-600 shadow-[0_0_0_4px_rgba(16,185,129,0.25)]" : "bg-red-500 border-red-600 shadow-[0_0_0_4px_rgba(239,68,68,0.25)]"}`}
                  title={ok ? "Cliquez pour repasser au rouge" : "Cliquez pour passer au vert"}
                  data-testid={`${testId}-btn`}
                />
                <div className="flex-1">
                  <div className={`text-sm font-semibold ${ok ? "text-emerald-200" : "text-red-200"}`}>{label}</div>
                  {extra && <div className="mt-1">{extra}</div>}
                </div>
                <span className={`ml-auto text-[10px] font-bold uppercase tracking-wider ${ok ? "text-emerald-300" : "text-red-300"}`}>
                  {ok ? "OK" : "À vérifier"}
                </span>
              </div>
            );
            return (
              <div className="space-y-2">
                <Row
                  ok={costsOk}
                  label="Tous les coûts de revient sont renseignés dans le devis Axonaut"
                  onToggle={(v) => toggle("costs_ok", v)}
                  testId="axonaut-costs"
                />
                <Row
                  ok={prodsOk}
                  label="Chaque produit Axonaut a photo + présentation complète (nombre de repas précisé)"
                  onToggle={(v) => toggle("products_ok", v)}
                  testId="axonaut-products"
                />
                <Row
                  ok={folderOk}
                  label="Le devis a été mis dans le dossier client/devis"
                  onToggle={(v) => toggle("folder_ok", v)}
                  testId="axonaut-folder"
                />
                {/* Bouton : demande de validation interne (bascule ON) */}
                {!devisPret && !event.quote_review_pending && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!costsOk || !prodsOk || !folderOk}
                    className="rounded-full border-2 border-primary text-primary hover:bg-primary/5 self-start font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={requestQuoteReview}
                    data-testid="request-quote-review-btn"
                    title={(!costsOk || !prodsOk || !folderOk) ? "Passez d'abord les 3 feux au vert" : "Notifier l'administrateur pour validation"}
                  >
                    <Send className="h-3.5 w-3.5 mr-1" /> Envoyer le devis en validation auprès du responsable
                  </Button>
                )}
                {/* Bouton : annuler la demande (bascule OFF) */}
                {!devisPret && event.quote_review_pending && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full border-2 border-amber-500 text-amber-200 hover:bg-amber-500/15 self-start font-semibold"
                    onClick={cancelQuoteReview}
                    data-testid="cancel-quote-review-btn"
                    title="Annuler la demande — la carte repassera en « Devis à faire »"
                  >
                    ↩︎ Annuler la demande de validation
                  </Button>
                )}
                <Row
                  ok={devisPret}
                  label="Devis prêt à envoyer (validation interne)"
                  onToggle={(v) => setCheckpoint("devis_pret", v)}
                  testId="devis-pret-toggle"
                  extra={
                    <>
                      {event.quote_review_pending && !devisPret && (
                        <Badge className="rounded-full bg-secondary text-secondary-foreground border-secondary text-[10px]">
                          ⏳ En attente de validation
                        </Badge>
                      )}
                      {devisPret && event.checkpoints?.devis_pret?.date && (
                        <span className="text-[11px] text-emerald-200">Coché le {event.checkpoints.devis_pret.date}</span>
                      )}
                    </>
                  }
                />
              </div>
            );
          })()}

          {/* Bouton dédié admin : valider le devis et le mettre en prêt à envoyer */}
          {!devisPret && event.quote_review_pending && (
            <Button
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold self-start"
              onClick={() => setCheckpoint("devis_pret", true)}
              data-testid="admin-validate-quote-btn"
            >
              <CheckCircle2 className="h-4 w-4 mr-2" /> Valider le devis · Le mettre en prêt à envoyer
            </Button>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Date d'envoi</Label>
              <Input
                type="date"
                value={offer.sent_at}
                onChange={e => {
                  const s = e.target.value;
                  const follow_up_at = offer.follow_up_at || (s ? toISODate(new Date(new Date(s + "T00:00:00").getTime() + 7 * 24 * 3600 * 1000)) : "");
                  setOffer(o => ({ ...o, sent_at: s, follow_up_at }));
                }}
                data-testid="offer-sent-at"
              />
            </div>
            <div>
              <Label className="text-xs">Montant HT proposé (€)</Label>
              <Input type="number" step="0.01" value={offer.amount} onChange={e => setOffer(o => ({ ...o, amount: e.target.value }))} data-testid="offer-amount" />
            </div>
            <div>
              <Label className="text-xs">Date de relance {!offer.follow_up_at && offer.sent_at && <span className="text-primary">(défaut J+7)</span>}</Label>
              <Input type="date" value={offer.follow_up_at} onChange={e => setOffer(o => ({ ...o, follow_up_at: e.target.value }))} data-testid="offer-follow-up" />
            </div>
          </div>
          {/* PDF(s) du devis à envoyer */}
          <div>
            <Label className="text-xs">PDF(s) du devis * (un ou plusieurs)</Label>
            <label className="mt-1 flex items-center gap-2 rounded-md border-2 border-dashed border-primary/40 bg-primary/5 px-3 py-3 cursor-pointer hover:bg-primary/10 transition" data-testid="offer-files-label">
              <Upload className="h-4 w-4 text-primary shrink-0" />
              <span className="flex-1 text-xs truncate">
                {(offer.files && offer.files.length > 0) ? (
                  <span className="font-semibold text-primary">{offer.files.length} fichier(s) : {offer.files.map(f => f.name).join(", ")}</span>
                ) : (
                  <span className="text-muted-foreground">Cliquez pour joindre un ou plusieurs PDF du devis</span>
                )}
              </span>
              <input
                type="file"
                accept="application/pdf"
                multiple
                className="hidden"
                onChange={e => setOffer(o => ({ ...o, files: Array.from(e.target.files || []) }))}
                data-testid="offer-files-input"
              />
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" className="rounded-full bg-primary" onClick={saveOffer} data-testid="offer-save">
              <Send className="h-3.5 w-3.5 mr-1" /> Offre envoyée
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Historique des versions du devis */}
      {versions.length > 0 && (
        <Card className="border-border/60 shadow-warm" data-testid="offer-versions-history">
          <CardContent className="p-5 space-y-3">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1">
              <History className="h-3 w-3 text-primary" /> Historique des versions envoyées ({versions.length})
            </div>
            <div className="rounded-lg border-2 border-amber-400 bg-amber-500/15 px-3 py-2 text-xs text-amber-200 font-semibold" data-testid="axonaut-new-version-warning">
              ⚠️ Toute nouvelle version de devis devra être traité dans Axonaut en duppliquant le devis de la version précédente et en refusant la version précédente du devis.
            </div>
            <div className="divide-y divide-border/60 border border-border/60 rounded-xl overflow-hidden">
              {versions.map((v, i) => {
                const isAccepted = acceptedVersion === v.version;
                const isLatest = i === versions.length - 1;
                const vFiles = v.files || [];
                return (
                  <div
                    key={`${v.version}-${i}`}
                    className={`px-4 py-3 ${isAccepted ? "bg-primary/10" : isLatest ? "bg-muted" : "bg-card"}`}
                    data-testid={`offer-version-${v.version}`}
                  >
                    <div className="flex items-center gap-3">
                      <Badge
                        className={`rounded-full ${isAccepted ? "bg-primary text-white border-primary" : "bg-card text-primary border-primary/40"} border font-bold`}
                      >
                        {v.version}
                      </Badge>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                          {Number(v.amount || 0).toLocaleString("fr-FR")} € HT
                          {isAccepted && (
                            <span className="text-xs text-primary font-bold flex items-center gap-1">
                              <Trophy className="h-3 w-3" /> Version acceptée
                            </span>
                          )}
                          {!isAccepted && isLatest && !v.refused && (
                            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Courante</span>
                          )}
                          {v.refused && (
                            <span className="text-[10px] uppercase tracking-widest text-red-300 font-bold flex items-center gap-1">
                              <XCircle className="h-3 w-3" /> Refusée{v.refused_at ? ` le ${v.refused_at}` : ""}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground tabular-nums flex flex-wrap gap-3 mt-0.5">
                          <span>Envoyée : {v.sent_at || "—"}</span>
                          {v.follow_up_at && <span>Relance : {v.follow_up_at}</span>}
                        </div>
                        {v.note && noteEdit[v.version] === undefined && <div className="text-xs italic text-muted-foreground mt-1">« {v.note} »</div>}
                        {v.refused_reason && <div className="text-xs italic text-red-300 mt-1">Motif de refus : « {v.refused_reason} »</div>}
                        {!v.refused && !isAccepted && v.version !== "v1" && (
                          <div className="mt-1.5 flex items-center gap-1.5" data-testid={`version-note-editor-${v.version}`}>
                            <Input
                              value={noteEdit[v.version] ?? v.note ?? ""}
                              onChange={e => setNoteEdit(prev => ({ ...prev, [v.version]: e.target.value }))}
                              placeholder="Ex : + location scène"
                              className="h-7 text-xs"
                              data-testid={`version-note-input-${v.version}`}
                            />
                            {noteEdit[v.version] !== undefined && noteEdit[v.version] !== (v.note || "") && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 rounded-full border-primary/40 text-primary hover:bg-primary/5"
                                onClick={() => saveVersionNote(v)}
                                data-testid={`version-note-save-${v.version}`}
                              >
                                <CheckCircle2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                      {isLatest && !v.refused && !event.client_offer?.accepted && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full border-red-500/50 text-red-300 hover:bg-red-500/15 h-8 text-[11px] shrink-0"
                          onClick={() => openRefuseDialog(v)}
                          data-testid={`refuse-version-${v.version}`}
                        >
                          <XCircle className="h-3 w-3 mr-1" /> Refuser cette version
                        </Button>
                      )}
                      {!v.refused && !isAccepted && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="rounded-full h-8 w-8 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/5 shrink-0"
                          onClick={() => cancelVersion(v)}
                          data-testid={`cancel-version-${v.version}`}
                          title="Annuler l'envoi de cette version"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                    {vFiles.length > 0 && (
                      <div className="mt-2 ml-14 space-y-1">
                        {vFiles.map(f => (
                          <div key={f.id} className="flex items-center gap-2 text-xs bg-card border border-border/50 rounded-md px-2 py-1" data-testid={`offer-version-file-${f.id}`}>
                            <FileText className="h-3 w-3 text-muted-foreground" />
                            <div className="flex-1 truncate">{f.name || f.original_name} <span className="text-muted-foreground">· {Math.round((f.size||0)/1024)} Ko</span></div>
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${f.file_id}`} target="_blank" rel="noreferrer" className="text-primary underline font-semibold" data-testid={`offer-version-file-open-${f.id}`}>Ouvrir</a>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {!event.client_offer?.accepted && versions.length > 1 && (
              <div className="rounded-lg border border-secondary/40 bg-secondary/10 px-3 py-2 text-xs flex flex-wrap items-center gap-2">
                <span className="font-semibold">Quelle version le client a-t-il validée ?</span>
                <select
                  value={acceptedVersionChoice || versions[versions.length - 1].version}
                  onChange={e => setAcceptedVersionChoice(e.target.value)}
                  className="h-8 rounded-md border border-border bg-background px-2 text-xs"
                  data-testid="offer-accepted-version-select"
                >
                  {versions.map(v => <option key={v.version} value={v.version}>{v.version} — {Number(v.amount || 0).toLocaleString("fr-FR")} €</option>)}
                </select>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Réponse client */}
      <Card className={`border-2 shadow-warm ${event.client_offer?.refused_final ? "border-red-400 bg-red-500/15" : event.client_offer?.accepted ? "border-primary bg-primary/5" : "border-secondary/60 bg-secondary/10"}`}>
        <CardContent className="p-5">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Réponse du client à l'offre</div>
              {event.client_offer?.refused_final ? (
                <div className="mt-2 flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-red-600" />
                  <div>
                    <div className="font-brand text-lg text-red-200">Proposition refusée</div>
                    <div className="text-xs text-muted-foreground">
                      Refusée le {event.client_offer.refused_at || "—"} — événement passé en « Événement annulé »
                    </div>
                    {event.client_offer.refused_reason && (
                      <div className="text-xs italic text-red-300 mt-0.5">« {event.client_offer.refused_reason} »</div>
                    )}
                  </div>
                </div>
              ) : event.client_offer?.accepted ? (
                <div className="mt-2 flex items-center gap-2">
                  <PartyPopper className="h-5 w-5 text-primary" />
                  <div>
                    <div className="font-brand text-lg text-primary">
                      Devis accepté
                      {event.client_offer.accepted_version && (
                        <span className="ml-2 text-sm font-normal">— {event.client_offer.accepted_version}</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">Le {event.client_offer.accepted_at} — étape « Devis validé » cochée automatiquement</div>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground mt-1 italic">En attente — clic sur le bouton dès que le client a validé.</div>
              )}
            </div>
            {!event.client_offer?.accepted && !event.client_offer?.refused_final && (
              <Button
                size="lg"
                className="rounded-full bg-primary shadow-glow active:scale-95"
                onClick={acceptOffer}
                data-testid="accept-offer-btn"
              >
                <Sparkles className="h-4 w-4 mr-2" /> Devis accepté
              </Button>
            )}
            {event.client_offer?.accepted && !event.client_offer?.refused_final && (
              <Button
                size="sm"
                variant="outline"
                className="rounded-full border-red-500/50 text-red-300 hover:bg-red-500/15"
                onClick={unacceptOffer}
                data-testid="unaccept-offer-btn"
              >
                <XCircle className="h-4 w-4 mr-2" /> Annuler la validation
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ==== Dialog : nouvelle version du devis ==== */}
      <Dialog open={versionDialogOpen} onOpenChange={setVersionDialogOpen}>
        <DialogContent className="max-w-md" data-testid="new-version-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-4 w-4 text-primary" />
              Nouvelle version du devis {newVersion.version && <Badge className="rounded-full bg-primary text-white ml-1">{newVersion.version}</Badge>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="text-xs text-muted-foreground bg-muted border border-border/50 rounded-md px-3 py-2">
              L'ancienne version reste dans l'historique. La nouvelle devient l'offre courante et l'acceptation client sera à re-confirmer.
            </div>
            <div>
              <Label className="text-xs">Date d'envoi de cette version *</Label>
              <Input type="date" value={newVersion.sent_at} onChange={e => setNewVersion(v => ({ ...v, sent_at: e.target.value }))} data-testid="new-version-sent-at" />
            </div>
            <div>
              <Label className="text-xs">Montant HT (€) *</Label>
              <Input type="number" step="0.01" value={newVersion.amount} onChange={e => setNewVersion(v => ({ ...v, amount: e.target.value }))} data-testid="new-version-amount" />
            </div>
            <div>
              <Label className="text-xs">Date de relance (défaut J+7 si vide)</Label>
              <Input type="date" value={newVersion.follow_up_at} onChange={e => setNewVersion(v => ({ ...v, follow_up_at: e.target.value }))} data-testid="new-version-follow-up" />
            </div>
            <div>
              <Label className="text-xs">Note / changements de cette version</Label>
              <Input value={newVersion.note} onChange={e => setNewVersion(v => ({ ...v, note: e.target.value }))} placeholder="Ex : Ajout d'une prestation" data-testid="new-version-note" />
            </div>
            <div>
              <Label className="text-xs">PDF du devis *</Label>
              <label className="mt-1 flex items-center gap-2 rounded-md border-2 border-dashed border-primary/40 bg-primary/5 px-3 py-3 cursor-pointer hover:bg-primary/10 transition" data-testid="new-version-file-label">
                <Upload className="h-4 w-4 text-primary shrink-0" />
                <span className="flex-1 text-xs truncate">
                  {newVersion.file ? (
                    <span className="font-semibold text-primary">{newVersion.file.name}</span>
                  ) : (
                    <span className="text-muted-foreground">Cliquez pour choisir le PDF du devis (obligatoire)</span>
                  )}
                </span>
                <input
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={e => setNewVersion(v => ({ ...v, file: e.target.files?.[0] || null }))}
                  data-testid="new-version-file-input"
                />
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setVersionDialogOpen(false)}>Annuler</Button>
            <Button className="rounded-full bg-primary" onClick={submitNewVersion} disabled={savingVersion} data-testid="new-version-submit">
              <Send className="h-4 w-4 mr-2" /> Envoyer {newVersion.version}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog : refus client du devis */}
      <Dialog open={refuseDialog.open} onOpenChange={v => !v && setRefuseDialog({ open: false, version: null, mode: "new_version", reason: "" })}>
        <DialogContent className="max-w-md" data-testid="refuse-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-600" /> Refus client{refuseDialog.version ? ` — ${refuseDialog.version}` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            <div className="text-xs text-muted-foreground bg-muted border border-border/50 rounded-md px-3 py-2">
              Choisissez la nature du refus du client. Le refus définitif fera passer l'événement dans la colonne « Événement annulé » du Kanban.
            </div>
            <div className="space-y-2">
              <label className={`flex items-start gap-2 rounded-lg border-2 px-3 py-2 cursor-pointer ${refuseDialog.mode === "new_version" ? "border-primary bg-primary/5" : "border-border hover:bg-muted"}`} data-testid="refuse-mode-new">
                <input type="radio" name="refuse-mode" checked={refuseDialog.mode === "new_version"}
                  onChange={() => setRefuseDialog(d => ({ ...d, mode: "new_version" }))} className="mt-1" />
                <div>
                  <div className="text-sm font-semibold">Le client souhaite une nouvelle proposition</div>
                  <div className="text-[11px] text-muted-foreground">La version courante est marquée refusée. Vous pouvez envoyer une nouvelle version.</div>
                </div>
              </label>
              <label className={`flex items-start gap-2 rounded-lg border-2 px-3 py-2 cursor-pointer ${refuseDialog.mode === "final" ? "border-red-400 bg-red-500/15" : "border-border hover:bg-muted"}`} data-testid="refuse-mode-final">
                <input type="radio" name="refuse-mode" checked={refuseDialog.mode === "final"}
                  onChange={() => setRefuseDialog(d => ({ ...d, mode: "final" }))} className="mt-1" />
                <div>
                  <div className="text-sm font-semibold text-red-200">Devis définitivement refusé</div>
                  <div className="text-[11px] text-muted-foreground">L'événement passe en statut « Proposition refusée » et va dans la colonne « Événement annulé ».</div>
                </div>
              </label>
            </div>
            <div>
              <Label className="text-xs">Motif du refus</Label>
              {refuseDialog.mode === "final" && (
                <div className="flex flex-wrap gap-1.5 mt-1 mb-2" data-testid="refuse-reason-presets">
                  {[
                    "Événement annulé par manque de budget",
                    "Événement annulé par changement d'avis du client",
                    "Autre prestataire sélectionné",
                  ].map(preset => {
                    const active = refuseDialog.reason === preset;
                    return (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => setRefuseDialog(d => ({ ...d, reason: preset }))}
                        className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition ${active ? "bg-red-600 text-white border-red-700" : "bg-card text-red-200 border-red-500/50 hover:bg-red-500/15"}`}
                        data-testid={`refuse-preset-${preset.slice(0,10).replace(/\s/g,'-')}`}
                      >
                        {preset}
                      </button>
                    );
                  })}
                </div>
              )}
              <Input value={refuseDialog.reason} onChange={e => setRefuseDialog(d => ({ ...d, reason: e.target.value }))} placeholder={refuseDialog.mode === "final" ? "Cliquez un motif ou saisissez librement…" : "Ex : le client demande une remise"} data-testid="refuse-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setRefuseDialog({ open: false, version: null, mode: "new_version", reason: "" })}>Annuler</Button>
            <Button className={`rounded-full text-white ${refuseDialog.mode === "final" ? "bg-red-600 hover:bg-red-700" : "bg-primary hover:bg-primary/90"}`} onClick={submitRefuse} data-testid="refuse-confirm">
              <XCircle className="h-4 w-4 mr-2" /> {refuseDialog.mode === "final" ? "Refus définitif" : "Marquer comme refusée"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog acompte à la validation d'une offre prestataire/artiste */}
      <Dialog open={depositDialog.open} onOpenChange={v => !v && setDepositDialog({ open: false, req: null, amount: "" })}>
        <DialogContent className="max-w-md" data-testid="deposit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-primary" /> Valider l'offre — acompte à payer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {depositDialog.req && (
              <div className="text-sm text-muted-foreground">
                Vous validez l'offre de <span className="font-semibold text-foreground">{depositDialog.req.name}</span>
                {depositDialog.req.offer_amount ? <> · <span className="font-brand text-primary">{Number(depositDialog.req.offer_amount).toLocaleString("fr-FR")} € HT</span></> : null}.
              </div>
            )}
            <div>
              <Label className="text-xs">Montant de l'acompte à payer (€ HT — laissez vide si aucun)</Label>
              <Input
                type="number" step="0.01" min="0"
                value={depositDialog.amount}
                onChange={e => setDepositDialog(d => ({ ...d, amount: e.target.value }))}
                placeholder="0.00"
                data-testid="deposit-amount-input"
                autoFocus
              />
              <div className="text-[11px] text-muted-foreground mt-1">
                L'acompte apparaîtra dans la tuile « Dépenses engagées » et pourra être marqué payé après règlement.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDepositDialog({ open: false, req: null, amount: "" })}>Annuler</Button>
            <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={confirmValidateWithDeposit} data-testid="deposit-confirm">
              <CheckCircle2 className="h-4 w-4 mr-2" /> Valider l'offre
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
